import { useDispatch, useSelector } from "react-redux";
import {
  setSaleStats,
  setMonthlySales,
  setDailySales,
  clearSaleDashboard,
} from "../../store/slices/dashboard/saleDashboardSlice";
import { AppDispatch, RootState } from "../../store/store";
import SaleDashboardService from "../../services/dashboard/SaleDashboardService";
import useApiErrorHandler from "../useApiErrorHandler";

/**
 * 🧭 Hook centralizado para manejar toda la lógica del dashboard de ventas.
 *
 * Permite:
 * - Cargar estadísticas generales (totales, por estado, usuario, etc.)
 * - Cargar estadísticas mensuales (por año)
 * - Cargar estadísticas diarias (por año y mes)
 * - Limpiar el estado global del dashboard
 */
export const useSaleDashboard = () => {
  const dispatch: AppDispatch = useDispatch();
  const { handle } = useApiErrorHandler();

  // ✅ Estados globales desde Redux
  const { stats, monthlyStats, dailyStats } = useSelector(
    (state: RootState) => state.saleDashboard
  );

  // ==========================================================
  // 📊 1️⃣ Cargar estadísticas generales del dashboard
  // ==========================================================
  const fetchStats = async () => {
    try {
      const response = await SaleDashboardService.getStats();
      if (response?.statusCode === 200) {
        dispatch(setSaleStats(response.data));
      }
    } catch (error: any) {
      handle(error);
    }
  };

  // ==========================================================
  // 📅 2️⃣ Cargar ventas mensuales (por año)
  // ==========================================================
  const fetchMonthlySales = async (year: number = new Date().getFullYear()) => {
    try {
      const response = await SaleDashboardService.getMonthlySalesByYear(year);
      if (response?.statusCode === 200) {
        dispatch(setMonthlySales(response.data));
      }
    } catch (error: any) {
      handle(error);
    }
  };

  // ==========================================================
  // 📆 3️⃣ Cargar ventas diarias (por año y mes)
  // ==========================================================
  const fetchDailySales = async (
    year: number = new Date().getFullYear(),
    month: number = new Date().getMonth() + 1
  ) => {
    try {
      const response = await SaleDashboardService.getDailySalesByYearAndMonth(
        year,
        month
      );
      if (response?.statusCode === 200) {
        dispatch(setDailySales(response.data));
      }
    } catch (error: any) {
      handle(error);
    }
  };

  // ==========================================================
  // 🔄 4️⃣ Limpiar todo el estado del dashboard
  // ==========================================================
  const resetDashboard = () => {
    dispatch(clearSaleDashboard());
  };

  // ==========================================================
  // 📦 Exportación de estados y métodos
  // ==========================================================
  return {
    // Estados globales
    stats,
    monthlyStats,
    dailyStats,

    // Métodos
    fetchStats,
    fetchMonthlySales,
    fetchDailySales,
    resetDashboard,
  };
};

export default useSaleDashboard;
